<template>
  <div class="letter-literature-container">
    <el-container>
      <el-main>
        <!-- 返回按钮和操作按钮 -->
        <div class="action-buttons">
          <el-button 
            type="text" 
            icon="el-icon-arrow-left" 
            @click="goBack"
          >
            返回
          </el-button>
          
          <el-button 
            type="primary" 
            icon="el-icon-edit" 
            @click="toggleEditMode"
            v-if="!isEditing"
          >
            修改
          </el-button>
          
          <template v-if="isEditing">
            <el-button 
              type="success" 
              icon="el-icon-check" 
              @click="saveChanges"
              :loading="saving"
            >
              保存
            </el-button>
            
            <el-button 
              type="info" 
              icon="el-icon-close" 
              @click="cancelEdit"
            >
              取消
            </el-button>
          </template>
        </div>

        <!-- 加载状态 -->
        <div v-if="loading" class="loading-container">
          <el-skeleton :rows="6" animated />
        </div>

        <!-- 错误状态 -->
        <div v-else-if="error" class="error-container">
          <el-alert :title="error" type="error" show-icon />
        </div>

        <!-- 查看模式 -->
        <div v-else-if="!isEditing" class="content-container">
          <!-- 图片展示 -->
          <div class="image-section">
            <el-image 
              :src="imageUrl" 
              fit="contain" 
              style="max-width: 100%; max-height: 500px;"
              :preview-src-list="[imageUrl]"
            />
          </div>

          <!-- 元数据展示 -->
          <div class="metadata-section">
            <el-card shadow="never">
              <div slot="header" class="clearfix">
                <span>文献详情</span>
              </div>

              <el-descriptions :column="1" border>
                <el-descriptions-item label="ID">{{ itemData.id || '无' }}</el-descriptions-item>
                <el-descriptions-item label="文件路径">{{ itemData.filepath || '无' }}</el-descriptions-item>
                <el-descriptions-item label="文件类型">{{ itemData.filetype || '无' }}</el-descriptions-item>
                <el-descriptions-item label="标题">{{ itemData.title || '无' }}</el-descriptions-item>
                <el-descriptions-item label="标签">{{ itemData.label || '无' }}</el-descriptions-item>
                <el-descriptions-item label="语言">{{ itemData.language || '无' }}</el-descriptions-item>
                <el-descriptions-item label="页数">{{ itemData.page || '无' }}</el-descriptions-item>
                <el-descriptions-item label="路径">{{ itemData.path || '无' }}</el-descriptions-item>
                <el-descriptions-item label="关联人物">{{ itemData.related_individuals || '无' }}</el-descriptions-item>
                <el-descriptions-item label="关联机构">{{ itemData.related_institutions || '无' }}</el-descriptions-item>
                <el-descriptions-item label="关联地点">{{ itemData.related_location || '无' }}</el-descriptions-item>
                <el-descriptions-item label="关联电影">{{ itemData.related_movies || '无' }}</el-descriptions-item>
                <el-descriptions-item label="来源">{{ itemData.source || '无' }}</el-descriptions-item>
                <el-descriptions-item label="规格">{{ itemData.specs || '无' }}</el-descriptions-item>
                <el-descriptions-item label="时间">{{ itemData.time || '无' }}</el-descriptions-item>
                <el-descriptions-item label="类型">{{ itemData.type || '无' }}</el-descriptions-item>
                <el-descriptions-item label="版本">{{ itemData.version || '无' }}</el-descriptions-item>
                <el-descriptions-item label="扫描文件">{{ itemData.scan_file || '无' }}</el-descriptions-item>
                <el-descriptions-item label="扫描人">{{ itemData.scan_man || '无' }}</el-descriptions-item>
                <el-descriptions-item label="扫描时间">{{ itemData.scan_time || '无' }}</el-descriptions-item>
                <el-descriptions-item label="备注" :span="2">{{ itemData.remark || '无' }}</el-descriptions-item>
              </el-descriptions>

              <!-- 内容展示 -->
              <div v-if="itemData.content" class="content-section">
                <h3>内容</h3>
                <div class="content-text">{{ itemData.content }}</div>
              </div>
            </el-card>
          </div>
        </div>

        <!-- 编辑模式 -->
        <div v-else class="edit-container">
          <el-card shadow="never">
            <div slot="header" class="clearfix">
              <span>编辑文献信息</span>
            </div>
            
            <el-form :model="editForm" label-width="120px">
              <el-form-item label="标题">
                <el-input v-model="editForm.title" />
              </el-form-item>
              
              <el-form-item label="标签">
                <el-input v-model="editForm.label" />
              </el-form-item>
              
              <el-form-item label="语言">
                <el-input v-model="editForm.language" />
              </el-form-item>
              
              <el-form-item label="页数">
                <el-input v-model.number="editForm.page" type="number" />
              </el-form-item>
              
              <el-form-item label="关联人物">
                <el-input v-model="editForm.related_individuals" />
              </el-form-item>
              
              <el-form-item label="关联机构">
                <el-input v-model="editForm.related_institutions" />
              </el-form-item>
              
              <el-form-item label="关联地点">
                <el-input v-model="editForm.related_location" />
              </el-form-item>
              
              <el-form-item label="关联电影">
                <el-input v-model="editForm.related_movies" />
              </el-form-item>
              
              <el-form-item label="来源">
                <el-input v-model="editForm.source" />
              </el-form-item>
              
              <el-form-item label="规格">
                <el-input v-model="editForm.specs" />
              </el-form-item>
              
              <el-form-item label="时间">
                <el-date-picker
                  v-model="editForm.time"
                  type="date"
                  placeholder="选择日期"
                  value-format="yyyy-MM-dd"
                />
              </el-form-item>
              
              <el-form-item label="类型">
                <el-input v-model="editForm.type" />
              </el-form-item>
              
              <el-form-item label="版本">
                <el-input v-model="editForm.version" />
              </el-form-item>
              
              <el-form-item label="扫描人">
                <el-input v-model="editForm.scan_man" />
              </el-form-item>
              
              <el-form-item label="扫描时间">
                <el-date-picker
                  v-model="editForm.scan_time"
                  type="date"
                  placeholder="选择日期"
                  value-format="yyyy-MM-dd"
                />
              </el-form-item>
              
              <el-form-item label="备注">
                <el-input 
                  v-model="editForm.remark" 
                  type="textarea" 
                  :rows="3"
                />
              </el-form-item>
              
              <el-form-item label="内容">
                <el-input 
                  v-model="editForm.content" 
                  type="textarea" 
                  :rows="6"
                />
              </el-form-item>
            </el-form>
          </el-card>
        </div>
      </el-main>
    </el-container>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'LetterLiterature',
  data() {
    return {
      itemData: {},
      loading: false,
      error: null,
      imageUrl: '',
      isEditing: false,
      saving: false,
      editForm: {
        title: '',
        label: '',
        language: '',
        page: '',
        related_individuals: '',
        related_institutions: '',
        related_location: '',
        related_movies: '',
        source: '',
        specs: '',
        time: '',
        type: '',
        version: '',
        scan_man: '',
        scan_time: '',
        remark: '',
        content: ''
      }
    };
  },
  created() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      const id = this.$route.query.id;
      if (!id) {
        this.error = '未指定文献ID';
        return;
      }
      this.loading = true;
      try {
        const response = await axios.get(`https://api.movie.yingshinet.com/collection`);
        if (response.data.c_data && response.data.c_data.length > 0) {
          this.itemData = response.data.c_data[id-1];
          this.imageUrl = this.getImageUrl(this.itemData.filepath);
          // 初始化编辑表单
          this.initEditForm();
        } else {
          this.error = '未找到指定文献';
        }
      } catch (error) {
        this.error = '加载数据失败: ' + error.message;
        console.error('获取数据失败:', error);
      } finally {
        this.loading = false;
      }
    },
    
    getImageUrl(filepath) {
      return filepath ? "https://assets.yingshinet.com/" + filepath.replace(/\\/g, '/') : '';
    },
    
    goBack() {
      this.$router.go(-1);
    },
    
    toggleEditMode() {
      this.isEditing = true;
    },
    
    initEditForm() {
      // 将itemData中的数据复制到editForm中
      const fields = [
        'id', // 添加ID字段
        'title', 'label', 'language', 'page', 'related_individuals',
        'related_institutions', 'related_location', 'related_movies',
        'source', 'specs', 'time', 'type', 'version', 'scan_man',
        'scan_time', 'remark', 'content'
      ];
      
      fields.forEach(field => {
          this.editForm[field] = this.itemData[field] || '';
        });
    },
    
    async saveChanges() {
      this.saving = true;
      try {
        // 准备要发送的数据，包含ID和所有编辑字段
        const jsonData = {
          id: this.itemData.id,  // 添加ID字段
          ...this.editForm       // 展开所有编辑字段
        };
        
        // 打印调试信息（可选）
        console.log('Sending data:', jsonData);

        // 发送 POST 请求（JSON 格式）
        const response = await axios.post(
          `https://api.movie.yingshinet.com/collection/update`,  // 注意URL已去掉ID参数
          jsonData,  // 直接传递 JSON 对象
          {
            headers: {
              'Content-Type': 'application/json'  // 明确指定 JSON 格式
            }
          }
        );
        
        if (response.data.success) {
          this.$message.success('更新成功');
          // 更新本地数据
          Object.assign(this.itemData, this.editForm);
          this.isEditing = false;
        } else {
          throw new Error(response.data.message || '更新失败');
        }
      } catch (error) {
        this.$message.error('更新失败: ' + error.message);
        console.error('更新失败:', error);
      } finally {
        this.saving = false;
      }
    },
    
    cancelEdit() {
      this.isEditing = false;
    }
  }
};
</script>

<style scoped>
.letter-literature-container {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.loading-container, .error-container {
  margin-top: 50px;
  text-align: center;
}

.content-container {
  margin-top: 20px;
}

.image-section {
  text-align: center;
  margin-bottom: 30px;
  background-color: #f5f5f5;
  padding: 20px;
  border-radius: 4px;
}

.metadata-section {
  margin-top: 30px;
}

.content-section {
  margin-top: 30px;
  padding: 20px;
  background-color: #f9f9f9;
  border-radius: 4px;
}

.content-text {
  white-space: pre-wrap;
  line-height: 1.6;
}

.el-descriptions {
  margin-top: 20px;
}

.clearfix:before,
.clearfix:after {
  display: table;
  content: "";
}

.clearfix:after {
  clear: both;
}
.action-buttons {
  margin-bottom: 20px;
  display: flex;
  gap: 10px;
}

.edit-container {
  margin-top: 20px;
}

.el-form {
  max-width: 800px;
  margin: 0 auto;
}

.el-input,
.el-textarea {
  width: 100%;
}

.el-date-picker {
  width: 100%;
}
</style>